import { useState, type FormEvent, type ReactNode } from 'react';
import { comisionHabitual, countryPreset, currencyInfo } from '@alexiarest/shared';
import { EMPIEZA_POR_TEXTO, guardarAlta, type AltaConAlexia } from '../altaConAlexia';
import {
  AltaError,
  confirmarCodigo,
  contrasenaValida,
  entrarConCorreo,
  entrarPorCognito,
  reenviarCodigo,
  registrarConCorreo,
} from '../auth/altaCorreo';
import { useAuth } from '../auth/AuthContext';
import { getCognitoConfig } from '../auth/cognito';
import './alta.css';

/** La landing, para «Cambiar algo» (el chat sigue donde lo dejó) y los textos legales. */
export const LANDING_URL = (import.meta.env.VITE_LANDING_URL ?? '').trim().replace(/\/+$/, '') || 'https://alexiarest.com';

/*
 * «Continuar con Google» queda FUERA por ahora (reunión Pol–Alex, 15 oct
 * 2026): la app móvil tendría que ofrecer también «Iniciar sesión con Apple».
 * El hueco está preparado: al encenderlo, sale arriba y con Google esta
 * pantalla no hace falta (la cuenta se crea al volver de Google).
 */
const GOOGLE_EN_ALTA = false;

/** Marco del alta: fondo blanco, logotipo arriba y el contenido centrado. */
export function AltaShell({ children, una = false, onSalir }: { children: ReactNode; una?: boolean; onSalir?: () => void }): JSX.Element {
  return (
    <div className="alta">
      <header className="alta__cabecera">
        <img className="alta__logo logo-tema--claro" src="/logo/logotipo-claro.svg" alt="Alexia Rest" width={145} height={32} />
        <img className="alta__logo logo-tema--oscuro" src="/logo/logotipo-oscuro.svg" alt="" aria-hidden="true" width={145} height={32} />
        {onSalir && <button type="button" className="alta__salir" onClick={onSalir}>Salir</button>}
      </header>
      <main className={`alta__cuerpo${una ? ' alta__cuerpo--uno' : ''}`}>{children}</main>
    </div>
  );
}

/** Lo que contó en el chat, tal cual se va a aplicar. */
export function ResumenDelChat({ alta }: { alta: AltaConAlexia }): JSX.Element {
  const preset = countryPreset(alta.pais);
  const moneda = alta.moneda ?? preset.currency;
  return (
    <div className="alta__ficha">
      <p className="alta__ficha-nombre">{alta.restaurante}</p>
      <dl>
        {alta.persona && <div className="alta__ficha-fila"><dt>Tu nombre</dt><dd>{alta.persona}</dd></div>}
        <div className="alta__ficha-fila">
          <dt>País</dt>
          <dd>
            {preset.label}
            <span className="alta__ficha-nota">
              {currencyInfo(moneda).label}
              {preset.generico ? ` · el ${preset.taxName.toLowerCase()} lo pones al entrar` : ` · ${preset.taxName} ${preset.vatPct} %`}
            </span>
          </dd>
        </div>
        {alta.delivery.length > 0 && (
          <div className="alta__ficha-fila">
            <dt>Apps</dt>
            <dd>
              {alta.delivery.join(', ')}
              <span className="alta__ficha-nota">
                Comisión de siempre ({alta.delivery.map((a) => `${a}\u00a0${comisionHabitual(a)}\u00a0%`).join(', ')}); la confirmas al entrar.
              </span>
            </dd>
          </div>
        )}
        <div className="alta__ficha-fila"><dt>Empiezas por</dt><dd>{EMPIEZA_POR_TEXTO[alta.empiezaPor]}</dd></div>
      </dl>
      <a className="alta__cambiar" href={`${LANDING_URL}/#empezar`}>Cambiar algo</a>
    </div>
  );
}

type Paso = { tipo: 'datos' } | { tipo: 'codigo'; email: string; password: string };

/**
 * Crear la cuenta (sección 16, pantalla 1). Resumen de lo que contó a la
 * izquierda y, a la derecha, correo y contraseña con la casilla de novedades
 * SIN marcar. Los correos obligatorios (prueba, cobro, errores) no dependen
 * de esa casilla. Cognito manda un código para confirmar el correo; con él,
 * la app entra sola y crea el restaurante con lo contado.
 */
export function AltaCuenta({ alta, demo = false }: { alta: AltaConAlexia; demo?: boolean }): JSX.Element {
  const { login, entrarConTokens } = useAuth();
  const config = getCognitoConfig();
  const [paso, setPaso] = useState<Paso>({ tipo: 'datos' });
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [mostrar, setMostrar] = useState(false);
  const [newsletter, setNewsletter] = useState(false);
  const [codigo, setCodigo] = useState('');
  const [ocupado, setOcupado] = useState(false);
  const [error, setError] = useState<AltaError | null>(null);
  const [aviso, setAviso] = useState('');

  const entrar = async (correo: string, clave: string) => {
    if (!config) return;
    try {
      const tokens = await entrarConCorreo(config, correo, clave);
      if (!entrarConTokens(tokens)) throw new AltaError('Sin sesión', 'sin-flujo');
    } catch (e) {
      // La cuenta ya existe y está confirmada: si aquí no se puede entrar
      // (flujo sin activar en este entorno, o un segundo factor), entra por
      // el formulario de Cognito con el correo puesto. Lo contado sigue guardado.
      if (e instanceof AltaError && e.code !== 'muchos-intentos') {
        await entrarPorCognito(config, correo);
        return;
      }
      throw e;
    }
  };

  const crear = async (event: FormEvent) => {
    event.preventDefault();
    setError(null);
    const correo = email.trim().toLowerCase();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(correo)) { setError(new AltaError('Escribe tu correo para crear la cuenta.', 'otro')); return; }
    if (!contrasenaValida(password)) { setError(new AltaError('La contraseña necesita al menos 10 caracteres, con mayúsculas, minúsculas y algún número.', 'contrasena')); return; }
    // La casilla viaja con lo contado: la cuenta nace con lo que marcó aquí.
    guardarAlta({ ...alta, newsletter });
    if (demo) {
      // La demo no crea cuentas: enseña el Panel del primer día con lo elegido.
      window.location.assign(`/?primer-dia=${alta.empiezaPor}`);
      return;
    }
    if (!config) return;
    setOcupado(true);
    try {
      const { confirmada } = await registrarConCorreo(config, { email: correo, password, nombre: alta.persona });
      if (confirmada) await entrar(correo, password);
      else setPaso({ tipo: 'codigo', email: correo, password });
    } catch (e) {
      setError(e instanceof AltaError ? e : new AltaError('No se pudo crear la cuenta. Vuelve a probar en un momento.', 'otro'));
    } finally {
      setOcupado(false);
    }
  };

  const confirmar = async (event: FormEvent) => {
    event.preventDefault();
    if (paso.tipo !== 'codigo' || !config) return;
    setError(null);
    setOcupado(true);
    try {
      try {
        await confirmarCodigo(config, paso.email, codigo);
      } catch (e) {
        // El código ya entró en un intento anterior y lo que falló fue entrar:
        // Cognito responde «...CONFIRMED». Está confirmada: a entrar.
        if (!(e instanceof AltaError && e.code === 'ya-confirmada')) throw e;
      }
      await entrar(paso.email, paso.password);
    } catch (e) {
      setError(e instanceof AltaError ? e : new AltaError('No se pudo confirmar el código. Vuelve a probar.', 'otro'));
      setOcupado(false);
    }
  };

  const reenviar = async (correo: string) => {
    if (!config) return;
    setError(null);
    try {
      await reenviarCodigo(config, correo);
      setAviso(`Te mandamos otro código a ${correo}.`);
    } catch (e) {
      // Ya confirmada: no hace falta código, se entra con la contraseña.
      if (e instanceof AltaError && e.code === 'ya-confirmada' && paso.tipo === 'codigo') {
        setOcupado(true);
        try { await entrar(correo, paso.password); } catch (e2) { setError(e2 instanceof AltaError ? e2 : new AltaError('No se pudo entrar. Vuelve a probar en un momento.', 'otro')); } finally { setOcupado(false); }
        return;
      }
      setError(e instanceof AltaError ? e : new AltaError('No se pudo mandar el código.', 'otro'));
    }
  };

  const fallo = error && (
    <div className="alta__error" role="alert">
      {error.code === 'sin-flujo' ? 'No se pudo entrar. Vuelve a probar en un momento.' : error.message}
      {error.code === 'existe' && paso.tipo === 'datos' && (
        <div className="alta__error-acciones">
          <button type="button" className="alta__enlace" onClick={() => login()}>Iniciar sesión</button>
          <button
            type="button"
            className="alta__enlace"
            onClick={() => {
              const correo = email.trim().toLowerCase();
              setPaso({ tipo: 'codigo', email: correo, password });
              void reenviar(correo);
            }}
          >
            Me llegó un código y no lo puse
          </button>
        </div>
      )}
    </div>
  );

  return (
    <AltaShell>
      <section className="alta__resumen" aria-labelledby="alta-titulo">
        <p className="alta__eyebrow">Último paso</p>
        <h1 id="alta-titulo" className="alta__titulo">Crea tu cuenta y entra con todo preparado</h1>
        <p className="alta__texto">
          {alta.persona ? `${alta.persona}, son` : 'Son'} 14 días gratis, sin tarjeta. Lo que me contaste ya está guardado.
        </p>
        <ResumenDelChat alta={alta} />
      </section>

      {paso.tipo === 'datos' ? (
        <form className="alta__form" onSubmit={(e) => void crear(e)} noValidate>
          {GOOGLE_EN_ALTA && (
            <>
              {/* Hueco de «Continuar con Google»: ver GOOGLE_EN_ALTA. */}
              <button type="button" className="btn btn--ghost alta__enviar" onClick={() => login('Google')}>Continuar con Google</button>
              <p className="alta__separador">o con tu correo</p>
            </>
          )}
          {fallo}
          <div className="alta__grupo">
            <label className="alta__etiqueta" htmlFor="alta-correo">Correo</label>
            <input id="alta-correo" className="alta__campo" type="email" autoComplete="email" inputMode="email" placeholder="tu@correo.com" value={email} onChange={(e) => setEmail(e.target.value)} required />
          </div>
          <div className="alta__grupo">
            <label className="alta__etiqueta" htmlFor="alta-clave">Contraseña</label>
            <div className="alta__clave">
              <input id="alta-clave" className="alta__campo" type={mostrar ? 'text' : 'password'} autoComplete="new-password" placeholder="Mínimo 10 caracteres" value={password} onChange={(e) => setPassword(e.target.value)} required aria-describedby="alta-clave-ayuda" />
              <button type="button" className="alta__mostrar" onClick={() => setMostrar((m) => !m)} aria-pressed={mostrar}>{mostrar ? 'Ocultar' : 'Mostrar'}</button>
            </div>
            <p id="alta-clave-ayuda" className="alta__ayuda">Mínimo 10 caracteres, con mayúsculas, minúsculas y algún número.</p>
          </div>
          <label className="alta__casilla">
            <input type="checkbox" checked={newsletter} onChange={(e) => setNewsletter(e.target.checked)} />
            Quiero recibir la newsletter y ofertas de Alexia. Los avisos de mi cuenta me llegan igualmente.
          </label>
          <button type="submit" className="btn alta__enviar" disabled={ocupado}>{ocupado ? 'Creando tu cuenta…' : 'Crear mi cuenta →'}</button>
          <p className="alta__legal">
            Al crear la cuenta aceptas los <a href={`${LANDING_URL}/terminos.html`} target="_blank" rel="noopener noreferrer">Términos</a> y la{' '}
            <a href={`${LANDING_URL}/privacidad.html`} target="_blank" rel="noopener noreferrer">Política de privacidad</a>.
            <br />
            ¿Ya tienes cuenta? <button type="button" className="alta__enlace" onClick={() => login()}>Inicia sesión</button>
          </p>
        </form>
      ) : (
        <form className="alta__form" onSubmit={(e) => void confirmar(e)}>
          <p className="alta__eyebrow">Confirma tu correo</p>
          <p className="alta__texto">Te mandé un código a <strong>{paso.email}</strong>. Escríbelo aquí y entras.</p>
          {fallo}
          {aviso && <p className="alta__ayuda" role="status">{aviso}</p>}
          <div className="alta__grupo">
            <label className="alta__etiqueta" htmlFor="alta-codigo">Código</label>
            <input id="alta-codigo" className="alta__campo alta__campo--codigo" inputMode="numeric" autoComplete="one-time-code" maxLength={8} value={codigo} onChange={(e) => setCodigo(e.target.value)} required />
          </div>
          <button type="submit" className="btn alta__enviar" disabled={ocupado || codigo.trim().length < 6}>{ocupado ? 'Entrando…' : 'Confirmar y entrar →'}</button>
          <p className="alta__legal">
            ¿No te llegó? Mira en spam o <button type="button" className="alta__enlace" onClick={() => void reenviar(paso.email)}>pide otro código</button>.
            <br />
            <button type="button" className="alta__enlace" onClick={() => { setPaso({ tipo: 'datos' }); setCodigo(''); setError(null); setAviso(''); }}>Usar otro correo</button>
          </p>
        </form>
      )}
    </AltaShell>
  );
}
